import api from "./client";
import type {
  Check,
  CheckDirection,
  CheckRiskEntry,
  CheckStatus,
  CheckSummary,
  CheckType,
  UpcomingChecksSummary,
} from "../types";

export interface CheckListParams {
  project_id?: number;
  direction?: CheckDirection;
  status?: CheckStatus;
  type?: CheckType;
  due_before?: string;
  due_after?: string;
}

export interface CheckListResponse {
  data: Check[];
  summary: CheckSummary;
}

export interface CheckFormData {
  project_id?: number | null;
  subcontract_id?: number | null;
  type: CheckType;
  direction: CheckDirection;
  bank_name?: string | null;
  check_no: string;
  drawer: string;
  amount: number;
  currency?: string;
  issue_date?: string | null;
  due_date: string;
  /** Yasal süre alanları (opsiyonel) */
  ibraz_deadline?: string | null;
  protesto_deadline?: string | null;
  zamanasimi_date?: string | null;
  reminder_days_before?: number;
  notes?: string | null;
  photo?: File | null;
}

// ---- Çek / Senet CRUD ----

/** Liste + özet (sayfalama yok; due_date ASC döner). */
export async function fetchChecks(params?: CheckListParams): Promise<CheckListResponse> {
  const response = await api.get("/api/v1/checks", { params });
  return { data: response.data.data, summary: response.data.summary };
}

export async function fetchCheck(checkId: number): Promise<Check> {
  const response = await api.get(`/api/v1/checks/${checkId}`);
  return response.data.data;
}

/** Oluşturma multipart'tır (opsiyonel çek/senet fotoğrafı). */
export async function createCheck(data: CheckFormData): Promise<Check> {
  const formData = new FormData();
  formData.append("type", data.type);
  formData.append("direction", data.direction);
  formData.append("check_no", data.check_no);
  formData.append("drawer", data.drawer);
  formData.append("amount", String(data.amount));
  formData.append("due_date", data.due_date);
  if (data.project_id) formData.append("project_id", String(data.project_id));
  if (data.subcontract_id) formData.append("subcontract_id", String(data.subcontract_id));
  if (data.bank_name) formData.append("bank_name", data.bank_name);
  if (data.currency) formData.append("currency", data.currency);
  if (data.issue_date) formData.append("issue_date", data.issue_date);
  if (data.ibraz_deadline) formData.append("ibraz_deadline", data.ibraz_deadline);
  if (data.protesto_deadline) formData.append("protesto_deadline", data.protesto_deadline);
  if (data.zamanasimi_date) formData.append("zamanasimi_date", data.zamanasimi_date);
  if (data.reminder_days_before !== undefined && data.reminder_days_before !== null) {
    formData.append("reminder_days_before", String(data.reminder_days_before));
  }
  if (data.notes) formData.append("notes", data.notes);
  if (data.photo) formData.append("photo", data.photo);

  const response = await api.post("/api/v1/checks", formData);
  return response.data.data;
}

/** Alan güncelleme + durum geçişi (durum makinesi backend'de zorlanır). */
export async function updateCheck(
  checkId: number,
  data: Partial<Omit<CheckFormData, "photo">> & { status?: CheckStatus },
): Promise<Check> {
  const response = await api.put(`/api/v1/checks/${checkId}`, data);
  return response.data.data;
}

export async function deleteCheck(checkId: number): Promise<void> {
  await api.delete(`/api/v1/checks/${checkId}`);
}

// ---- C9: Vadesi yaklaşanlar (dashboard widget'i) + OCR okuma ----

export interface UpcomingChecksResponse {
  data: Check[];
  summary: UpcomingChecksSummary;
}

/** Açık durumda ve vadesi `days` gün içinde olan kayıtlar + widget özeti. */
export async function fetchUpcomingChecks(days = 30): Promise<UpcomingChecksResponse> {
  const response = await api.get("/api/v1/checks/upcoming", { params: { days } });
  return { data: response.data.data, summary: response.data.summary };
}

export interface ScanCheckResponse {
  check: Check;
  /** OCR ile doldurulan boş alanlar (örn. ['amount', 'due_date']) */
  filled_fields: string[];
  message: string;
}

/**
 * Çek fotoğrafını OCR'dan geçirir (senkron, ~3-8 sn; 10 istek/saat throttle).
 * Yalnız BOŞ alanlar doldurulur; dolu alanlar korunur. file_id yoksa 422.
 */
export async function scanCheck(checkId: number): Promise<ScanCheckResponse> {
  const response = await api.post(`/api/v1/checks/${checkId}/scan`);
  return {
    check: response.data.data,
    filled_fields: response.data.filled_fields ?? [],
    message: response.data.message ?? "",
  };
}

// ---- Cari (keşideci) risk özeti (yalnız admin/yonetici) ----

/** Cari bazlı risk skoru listesi; skor desc, drawer asc sıralı döner. */
export async function fetchCheckRiskSummary(): Promise<CheckRiskEntry[]> {
  const response = await api.get("/api/v1/checks/risk-summary");
  return response.data.data;
}
